/*
Updates to this file should also be reflected in NewSelfServeInstructions.tsx
*/
// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';

import { Project } from 'dashboard/types';
import API from '@scale/frontend-lib/dashboard-api';
import { createEditor, Editor, Transforms, Element as SlateElement } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, Editable, withReact } from 'slate-react'; // Slate components, React plugin
import EditorToolbar from './EditorToolbar';
import type { Types } from 'mongoose';

import { IframeSection } from './IframeSection';
import { ImageSection } from './ImageSection';
import { LabelSection } from './LabelSection';
import ExampleLabelModal from './ExampleLabelModal';
import { Prompt } from 'react-router-dom';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const VOID_TYPES = ['label-section', 'iframe']; // element types that are void (non-editable). note slate won't render their children...

interface SelfServeInstructionsProps {
  blocks: any;
  setBlocks: $FIXME;
  project?: Project;
  uploadedAssets: $FIXME;
  taxonomy: $FIXME;
  readOnly: $FIXME;
  hasSensitiveContent?: boolean;
  handleSave?: Function;
  isDirty?: boolean;
  isEdgeCase?: boolean;
  setInfoMsg?: (s: string) => void;
  updatedAt?: string;
  hasOpenedExample?: (taskId: string, isGoodExample: boolean) => void;
  projectTemplateId?: Types.ObjectId;
}

const SelfServeInstructions: React.FC<SelfServeInstructionsProps> = ({
  blocks,
  setBlocks,
  project,
  uploadedAssets,
  taxonomy,
  readOnly,
  hasSensitiveContent = false,
  handleSave,
  isDirty = false,
  isEdgeCase = false,
  setInfoMsg,
  updatedAt,
  hasOpenedExample,
  projectTemplateId,
}) => {
  const [location, setLocation] = useState(null);
  const [lastSelection, setLastSelection] = useState(null);
  const [exampleModalOpen, setExampleModalOpen] = useState(false);

  // Create a Slate editor object that won't change across renders
  const editor = useMemo(() => withEditableVoids(withHistory(withReact(createEditor()))), []);
  const renderElement = useCallback(
    (props: any) => (
      <Element
        {...props}
        setInfoMsg={setInfoMsg}
        readOnly={readOnly}
        location={location}
        setLocation={setLocation}
        setExampleModalOpen={setExampleModalOpen}
        hasOpenedExample={hasOpenedExample}
        projectTemplateId={projectTemplateId}
      />
    ),
    [readOnly, location],
  );

  const disableAdultContent = async () => {
    const { isWorker } = await API.selfserve.adultContentToggle({ blockAdultContent: true });
    if (isWorker) {
      window.location.href = '/';
    }
  };
  // Define a leaf rendering function (also memoized)
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);

  return (
    <div className="slate">
      {!!project?.taxonomy?.param_chunks && (
        <h1 style={{ color: 'blue' }}>
          This project contains different types of tasks. Please refer to the relevant instruction
          sections for the questions and annotations you are given.
        </h1>
      )}
      {isEdgeCase && (
        <h1 style={{ color: 'orange' }}>
          This is an edge case detection task. The primary goal is to determine whether the
          following instructions are detailed and specific enough to complete the task WITHOUT
          AMBIGUITY. Please provide your feedback in the "edge case feedback" modal at the bottom of
          the task page.
        </h1>
      )}
      {hasSensitiveContent && (
        <h1 style={{ color: 'red' }}>
          WARNING: this project may contain sensitive content. If you're not comfortable with this,
          please <a onClick={disableAdultContent}>exit now</a> to work on other projects.
        </h1>
      )}
      {!readOnly && (
        <Prompt
          when={isDirty}
          message={'Are you sure you want to leave? Your changes have not been saved.'}
        />
      )}
      <Slate editor={editor} value={blocks} onChange={newValue => setBlocks(newValue)}>
        {exampleModalOpen && (
          <ExampleLabelModal
            modalOpen={exampleModalOpen}
            setModalOpen={arg => setExampleModalOpen(arg)}
            project={project}
            uploadedAssets={uploadedAssets}
            taxonomy={taxonomy}
            location={location}
            lastSelection={editor.selection}
            handleSave={isDirty ? handleSave : undefined}
          />
        )}
        {!readOnly && (
          <EditorToolbar
            isMarkActive={isMarkActive}
            isBlockActive={isBlockActive}
            toggleMark={toggleMark}
            toggleBlock={toggleBlock}
            lastSelection={lastSelection}
            handleSave={handleSave}
            shouldShowLabeledExamples={taxonomy && uploadedAssets}
            updatedAt={updatedAt}
          />
        )}

        <div style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <Editable
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder="Type here..."
            spellCheck
            autoFocus
            readOnly={readOnly}
            onBlur={() => {
              setLastSelection(editor.selection);
            }}
          />
        </div>
      </Slate>
      <style jsx global>{`
        .slate section {
          border-top-right-radius: 0px !important;
          width: 100%;
          height: 100%;
          border-bottom-right-radius: 0px !important;
          border: 0px !important;
          border-left: 0;
          border-top: 0;
          margin-top: 0px !important;
        }

        .slate {
          color: black;
        }
      `}</style>
    </div>
  );
};

// allow some elements to be non-editable
const withEditableVoids = editor => {
  const { isVoid } = editor;
  editor.isVoid = element => {
    return VOID_TYPES.includes(element.type) ? true : isVoid(element);
  };
  return editor;
};

// handles rendering a given element (based on its properties)
const Element = props => {
  const { attributes, children, element } = props;

  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote
          {...attributes}
          style={{
            borderLeft: '2px solid #ddd',
            marginLeft: 0,
            marginRight: 0,
            paddingLeft: '10px',
            color: '#666',
            fontStyle: 'italic',
          }}
        >
          {children}
        </blockquote>
      );
    case 'bulleted-list':
      return <ul {...attributes}>{children}</ul>;
    case 'heading-1':
      // trying to add section with ID that can be scrolled to
      // TODO: once this works add these changes to heading-2 as well
      if (
        children &&
        children.props &&
        children.props.node &&
        children.props.node.children.length > 0
      ) {
        return (
          <section
            id={'#' + children.props.node.children[0].text.split(' ').join('_') + '-h1'}
            {...attributes}
          >
            <h1>{children}</h1>
          </section>
        );
      }
      return <h1 {...attributes}>{children}</h1>;
    case 'heading-2':
      return <h2 {...attributes}>{children}</h2>;
    case 'heading-3':
      return <h3 {...attributes}>{children}</h3>;
    case 'heading-4':
      return <h4 {...attributes}>{children}</h4>;
    case 'list-item':
      return <li {...attributes}>{children}</li>;
    case 'numbered-list':
      return <ol {...attributes}>{children}</ol>;
    case 'label-section':
      return <LabelSection {...props}>{children}</LabelSection>;
    case 'image':
      return <ImageSection {...props}>{children}</ImageSection>;
    case 'iframe':
      return <IframeSection {...props}>{children}</IframeSection>;
    default:
      return <p {...attributes}>{children}</p>;
  }
};

/*
 * Define component to render leaves with (optional) formatting
 *   (For every format you add, Slate will break up the text
 *   content into "leaves", you need to tell Slate how to read it
 *   just like for elements.)
 */
const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.code) children = <code>{children}</code>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  return <span {...attributes}>{children}</span>;
};

// toggle a given formatting style (when button in toolbar is clicked)
// (operates on current selection or location of cursor)
const toggleMark = (editor, format) => {
  if (isMarkActive(editor, format)) Editor.removeMark(editor, format);
  else Editor.addMark(editor, format, true);
};

// looks at current selection or cursor location
// and identifies whether the node(s)? have a given text format (e.g. 'bold')
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// looks at current selection or cursor location
// and identifies whether the node(s)? have a given block level formatting (e.g. 'numbered-list')
const isBlockActive = (editor, format) => {
  const [match] = Editor.nodes(editor, {
    match: n => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
  });
  return !!match;
};
const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(editor, format);
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n => LIST_TYPES.includes(!Editor.isEditor(n) && SlateElement.isElement(n) && n.type),
    split: true,
  });
  const newProperties = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  };
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export default SelfServeInstructions;
